import {
  creditClient,
  getCreditPullInfo,
  initializeCreditSDK,
  setCreditOnDemand,
  setProviderBureauData,
} from '../actions/credit';
import {getLangCode, stringBundle} from '../stringBundle';
import {useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {RootState} from '../reducers/rootReducer';
import {processCreditPull} from '../helpers/creditHelper';
import utils from '../helpers/utils';

/**
 * Custom hook to handle credit data fetching and processing
 * @returns Credit data related methods and state
 */
export const useCreditData = () => {
  const dispatch = useDispatch();
  const auth = useSelector((store: RootState) => store.auth);
  const credit: CreditStore = useSelector((store: RootState) => store.credit);
  const [hasDispatched, setHasDispatched] = useState(false);
  const accountId = utils.safeGet(auth, 'user.primaryMember.accountId', '') || '';
  let hasEquifaxCredit = false;
  let hasTuCACredit = false;
  const country = utils.safeGet(auth, 'user.countryCode', 'US');

  // Function to fetch credit data from credit sdk
  const unifiedCredit = useCallback(
    (is3BUser: boolean, creditPullInfo: CreditPullInfo, accountId: string) => {
      if (!hasDispatched) {
        const showLLOnboardingCredit = utils.safeGet(auth, 'user.primaryMember.showLLOnboardingCredit', false) || false;
        const showLLDormantOnboarding =
          utils.safeGet(auth, 'user.primaryMember.showLLDormantOnboarding', false) || false;
        const isPull1BReportAllowed = utils.safeGet(creditPullInfo, 'isPull1BReportAllowed', false) || false;
        const localizedStringBundle = stringBundle(getLangCode(), null);
        const bureau = hasEquifaxCredit ? 'EFX' : 'TU';

        // pull latest credit score and report for dormant/reengage onboarding user if it is available
        if (hasEquifaxCredit && isPull1BReportAllowed && (showLLOnboardingCredit || showLLDormantOnboarding)) {
          processCreditPull(creditPullInfo, 'EFX', is3BUser, {dispatch, auth}, localizedStringBundle, bureau, country);
        } else {
          dispatch(
            creditClient(localizedStringBundle, getLangCode(), is3BUser, accountId, bureau, country, creditPullInfo)
          );
        }
        return setHasDispatched(true);
      }
    },
    [dispatch, hasDispatched, auth, credit]
  );

  // Function to initialize credit data
  const initCreditDataForUnified = useCallback(async() => {
    const is3BScoreFeatureAvailable =
      utils.safeGet(auth, 'user.primaryMember.plan.productFeatures.creditScore_3B.featureStatus') === 'AVAILABLE';
    const is3BScoreFulfillmentEnrolled =
      utils.safeGet(auth, 'user.primaryMember.plan.productFeatures.creditScore_3B.fulfillmentStatus') === 'ENROLLED';
    const is3BUser = is3BScoreFeatureAvailable && is3BScoreFulfillmentEnrolled;
    const accountId = utils.safeGet(auth, 'user.primaryMember.accountId', '') || '';
    dispatch(setCreditOnDemand());
    await initializeCreditSDK({dispatch, accountId});

    try {
      const res = await dispatch(getCreditPullInfo(accountId));
      const isFulfilled1BReportAvailable = utils.safeGet(res, 'pullInfo.isFulfilled1BReportAvailable', false);
      const isFulfilled3BReportAvailable = utils.safeGet(res, 'pullInfo.isFulfilled3BReportAvailable', false);
      const creditPullInfo = utils.safeGet(res, 'pullInfo', {});
      if (hasTuCACredit) {
        const localizedStringBundle = stringBundle(getLangCode(), null);
        dispatch(
          creditClient(localizedStringBundle, getLangCode(), is3BUser, accountId, 'TU', country, creditPullInfo)
        );
      } else if (isFulfilled1BReportAvailable || isFulfilled3BReportAvailable) {
        unifiedCredit(is3BUser, creditPullInfo, accountId);
      }
    } catch (error) {
      console.error('Error fetching credit pull info:', error);
    }
  }, [auth, dispatch, unifiedCredit]);

  // Function to initialize credit data
  const initCreditData = useCallback(() => {
    const isLoading = utils.safeGet(auth, 'isLoading');
    if (!isLoading) {
      const productFeatures = utils.safeGet(auth, 'user.primaryMember.plan.productFeatures', '');
      const creditScore1BFeatureStatus = utils.safeGet(productFeatures, 'creditScore_1B.featureStatus', '');
      const creditScore1BFulfillmentStatus = utils.safeGet(productFeatures, 'creditScore_1B.fulfillmentStatus', '');
      const creditScore3BFeatureStatus = utils.safeGet(productFeatures, 'creditScore_3B.featureStatus', '');
      const creditScore3BFullfilmentStatus = utils.safeGet(productFeatures, 'creditScore_3B.fulfillmentStatus', '');
      const creditScore1bCaFeatureStatus = utils.safeGet(productFeatures, 'creditScore1bCa.featureStatus', '');
      const creditScore1bCaFullfilmentStatus = utils.safeGet(productFeatures, 'creditScore1bCa.fulfillmentStatus', '');
      hasEquifaxCredit =
        creditScore1bCaFeatureStatus !== 'AVAILABLE' &&
        ((creditScore1BFeatureStatus === 'AVAILABLE' && creditScore1BFulfillmentStatus === 'ENROLLED') ||
          (creditScore3BFeatureStatus === 'AVAILABLE' && creditScore3BFullfilmentStatus === 'ENROLLED'));
      hasTuCACredit =
        creditScore1bCaFeatureStatus !== 'NOT_AVAILABLE' &&
        creditScore1bCaFeatureStatus === 'AVAILABLE' &&
        creditScore1bCaFullfilmentStatus === 'ENROLLED';

      if (creditScore1bCaFeatureStatus !== 'NOT_AVAILABLE') {
        dispatch(setProviderBureauData('TU', '900'));
      }
      if (
        hasEquifaxCredit ||
        creditScore1BFeatureStatus !== 'NOT_AVAILABLE' ||
        creditScore3BFeatureStatus !== 'NOT_AVAILABLE'
      ) {
        dispatch(setProviderBureauData('EFX', '850'));
      }
      if ((hasEquifaxCredit || hasTuCACredit) && accountId) {
        initCreditDataForUnified();
      }
    }
  }, [auth, dispatch, accountId]);

  return {
    initCreditData,
    initCreditDataForUnified,
  };
};
